import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@core/providers/http';
import { map, Observable } from 'rxjs';

export interface CreateGrupoRequest {
  nomeGrupo: string;
  descricao?: string;
}
@Injectable({
  providedIn: 'root',
})
export class GrupoPermissoesService {
  private readonly path = 'profissionais';
  public params = new HttpParams();

  constructor(private apiService: ApiService) {}

  // List all groups
  list(params?: any): Observable<any> {
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/grupos`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
        true // <- força o uso da URL secundária
      )
      .pipe(
        // debounceTime(500),
        map((response) => response)
      );
  }

  listUser(params?: any): Observable<any> {
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/usuarios/roles`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
        true // <- força o uso da URL secundária
      )
      .pipe(
        // debounceTime(500),
        map((response) => response)
      );
  }

  listGroupsByUsername(username: string): Observable<any> {
    const params = new HttpParams().set('username', username);
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/usuarios/grupos`,
        params,
        true // <- força o uso da URL secundária
      )
      .pipe(
        // debounceTime(500),
        map((response) => response)
      );
  }

  // List all groups and roles
  listAndRoles(params?: any): Observable<any> {
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/grupos-e-roles`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
        true // <- força o uso da URL secundária
      )
      .pipe(
        // debounceTime(500),
        map((response) => response)
      );
  }

  listAllRoles(params?: any): Observable<any> {
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/todas-roles`,
        params
          ? new HttpParams({
              fromObject: params,
            })
          : this.params,
        true // <- força o uso da URL secundária
      )
      .pipe(
        // debounceTime(500),
        map((response) => response)
      );
  }

  listRoleForGroupID(groupId: string): Observable<any> {
    return this.apiService
      .get<{}>(
        `${this.path}/api/KeyKloakController/grupos-e-roles/${groupId}`,
        this.params,
        true // <- força o uso da URL secundária
      )
      .pipe(map((response) => response));
  }

  deleteGroup(groupId: string): Observable<any> {
    return this.apiService
      .delete(
        `${this.path}/api/KeyKloakController/grupos/${groupId}`,
        undefined,
        true // <- força o uso da URL secundária
      )
      .pipe(map((response) => response));
  }

  deleteRoleByGroupId(groupId: string, roleId: string): Observable<any> {
    return this.apiService.delete(
      `${this.path}/api/KeyKloakController/grupos/${groupId}/roles/${roleId}/remove`,
      undefined,
      true
    );
  }

  /**
   * Create a new group
   * @param formData FormData containing new goup data
   * @returns Observable<any>
   */
  createGrupo(nomeGrupo: string, descricao?: string): Observable<any> {
    const params = new HttpParams()
      .set('nomeGrupo', (nomeGrupo ?? '').trim())
      .set('descricao', (descricao ?? '').trim());

    // body null e params nas opções
    return this.apiService.post(
      `${this.path}/api/KeyKloakController/grupos/criar`,
      undefined,
      { params },
      true
    );
  }

  updateGroup(id: string, name: string, descricao?: string): Observable<any> {
    return this.apiService.put(
      `${this.path}/api/KeyKloakController/grupos/update`,
      { id, name, descricao },
      undefined,
      true
    );
  }

  /**
   * Save roles for a group
   * @param groupId ID do grupo
   * @param payload Payload contendo os IDs das roles
   * @returns Observable<any>
   */
  public saveGroupRoles(groupId: string, roleIds: string[]): Observable<any> {
    return this.apiService.post(
      `${this.path}/api/KeyKloakController/grupos/${groupId}/roles/add`,
      roleIds,
      undefined,
      true
    );
  }

  /**
   * Atribui um usuário a um grupo
   * @param username Nome de usuário a ser atribuído
   * @param groupId ID do grupo
   * @returns Observable<any>
   */
  atribuirUser({ username, groupId }: any): Observable<any> {
    const params = new HttpParams()
      .set('username', username)
      .set('groupId', groupId);

    // repare que o segundo argumento é um body vazio ({}); o querystring fica em `params`
    return this.apiService.put(
      `${this.path}/api/KeyKloakController/usuarios/atribuir-grupo`,
      {}, // corpo vazio
      params, // HttpParams usados como query params
      true // forceSecondary, se precisar
    );
  }

  /**
   * Desassocia um usuário de um grupo
   * @param username Nome de usuário a ser desassociado
   * @param groupId ID do grupo
   * @returns Observable<any>
   */
  desassociarUser({ username, groupId }: any): Observable<any> {
    const params = new HttpParams()
      .set('username', username)
      .set('groupId', groupId);

    // repare que o segundo argumento é um body vazio ({}); o querystring fica em `params`
    return this.apiService.delete(
      `${this.path}/api/KeyKloakController/usuarios/remover-grupo-user`,
      params, // HttpParams usados como query params
      true // forceSecondary, se precisar
    );
  }

  /**
   * Cria um novo usuário
   * @param userData Dados do usuário a ser criado
   * @returns Observable<any>
   */
  createUser(userData: any): Observable<any> {
    return this.apiService.post(
      `${this.path}/api/KeyKloakController/usuarios/Add`,
      userData,
      undefined,
      true
    );
  }

  /**
   * Recupera a senha de um usuário
   * @param username username do usuário
   * @param email Email do usuário
   * @param novasenha nova senha a ser definida
   * @returns Observable<any>
   */

  recoveryPassword(
    username: string,
    email: string,
    novasenha: string
  ): Observable<any> {
    const params = `?username=${username}&email=${email}&novasenha=${novasenha}`;
    return this.apiService.post(
      `${this.path}/api/KeyKloakController/Redefinir-senhas${params}`,
      {}, // corpo vazio
      undefined,
      true
    );
  }

  habilitarUser(username: string): Observable<any> {
    const params = new HttpParams().set('username', username);
    return this.apiService.postQueryParams(
      `${this.path}/api/KeyKloakController/usuarios/habilitar`,
      {}, // corpo vazio
      params,
      true
    );
  }

  desabilitarUser(username: string): Observable<any> {
    const params = new HttpParams().set('username', username);
    return this.apiService.postQueryParams(
      `${this.path}/api/KeyKloakController/usuarios/desabilitar`,
      {}, // corpo vazio
      params,
      true
    );
  }
}
